export class LayerEventHandler {
  map: mapboxgl.Map;
  handlers: Map<string, Map<string, Set<any>>>;
  defaultHandlers: Map<string, Set<any>>;

  constructor(map) {
    this.map = map;

    this.handlers = new Map();
    this.defaultHandlers = new Map();
  }

  getEventLayers(eventName) {
    if (!this.handlers.has(eventName)) {
      return [];
    }

    return Array.from(this.handlers.get(eventName)?.keys() || []);
  }

  // false return value of event listener callback stops bubbling
  on(eventName, layerId, callback) {
    if (!this.defaultHandlers.has(eventName) && !this.handlers.has(eventName)) {
      // Register a map event for the given event name
      this.map.on(eventName, this._onMapEvent);
    }

    if (!layerId) {
      if (!this.defaultHandlers.has(eventName)) {
        this.defaultHandlers.set(eventName, new Set());
      }

      // layerId is not specified, so this is a 'default handler' that will be called if no other events have cancelled the event
      this.defaultHandlers.get(eventName)?.add(callback);

      return;
    }

    if (!this.handlers.has(eventName)) {
      this.handlers.set(eventName, new Map());
    }

    if (!this.handlers.get(eventName)?.get(layerId)) {
      this.handlers.get(eventName)?.set(layerId, new Set());
    }

    this.handlers.get(eventName)?.get(layerId)?.add(callback);
  }

  off(eventName, layerId, callback) {
    if (layerId) {
      if (this.handlers.get(eventName)?.get(layerId)) {
        this.handlers.get(eventName)?.get(layerId)?.delete(callback);

        if (!this.handlers.get(eventName)?.get(layerId)?.size) {
          this.handlers.get(eventName)?.delete(layerId);
        }

        if (!this.handlers.get(eventName)?.size) {
          this.handlers.delete(eventName);
        }
      }
    } else {
      if (this.defaultHandlers.has(eventName)) {
        this.defaultHandlers.get(eventName)?.delete(callback);

        if (!this.defaultHandlers.get(eventName)?.size) {
          this.defaultHandlers.delete(eventName);
        }
      }
    }

    if (!this.defaultHandlers.has(eventName) && !this.handlers.has(eventName)) {
      // Register a map event for the given event name
      this.map.off(eventName, this._onMapEvent);
    }
  }

  _onMapEvent = (event) => {
    let layers = this.getEventLayers(event.type); // unordered list of layers to be checked

    const eventName = event.type;

    // filter only layers that are on the map now, so features can be queried
    layers = layers.filter((layer) => this.map.getLayer(layer));
    if (!layers.length) {
      return;
    }

    // This gets the features that was clicked in the correct layer order
    const eventFeatures = this.map.queryRenderedFeatures(event.point, {
      layers: layers,
    });

    const eventFeaturesByLayerMap = eventFeatures.reduce((acc, feature) => {
      if (acc.has(feature.layer.id)) {
        acc.get(feature.layer.id).push(feature);
      } else {
        acc.set(feature.layer.id, [feature]);
      }

      return acc;
    }, new Map());

    // This makes a sorted array of the layers that are clicked, ES6 map preserves insertion order, so layers are sorted
    const sortedLayers = Array.from(eventFeaturesByLayerMap.keys());

    let bubbleEvent = true;

    // Loop through each of the sorted layers starting with the first (top-most clicked layer)
    // Call the handler for each layer in order, and potentially stop propagating the event
    for (let i = 0; i < sortedLayers.length; i++) {
      if (!bubbleEvent) {
        break;
      }

      const layerId = sortedLayers[i];
      const layerFeatures = eventFeaturesByLayerMap.get(layerId);

      // Call the layer handlers for this layer, giving the clicked features
      for (const cbk of this.handlers.get(eventName)?.get(layerId) || []) {
        const cbkResult = cbk(event, layerFeatures);

        if (cbkResult === false) {
          bubbleEvent = false;
          return;
        }
      }
    }

    // No events has cancelled the bubbling
    if (bubbleEvent === true) {
      this.defaultHandlers.get(eventName)?.forEach((handler) => {
        handler(event);
      });
    }
  };
}
